import { useEffect, useState } from "react";
import { Platform } from "react-native";

// Pixels the mouse moves before a press becomes a drag.
const DRAG_THRESHOLD = 4;

// Web: a horizontal ScrollView scrolls with the mouse wheel, which only moves
// up and down, and by dragging it with the mouse, as it does with a finger or
// a trackpad. At either end the wheel scrolls the page as usual. A drag that
// ends on a chip doesn't press it. Returns the ScrollView's ref (a callback, so
// a row that mounts later still gets it).
const useMouseScroll = () => {
  const [scrollView, setScrollView] = useState(null);
  useEffect(() => {
    if (Platform.OS !== "web" || !scrollView) return undefined;
    const node = scrollView.getScrollableNode?.() || scrollView;
    if (!node?.addEventListener) return undefined;
    const overflows = () => node.scrollWidth > node.clientWidth + 1;

    const onWheel = (e) => {
      if (!overflows() || e.ctrlKey) return;
      const delta =
        Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      const max = node.scrollWidth - node.clientWidth;
      const next = Math.min(max, Math.max(0, node.scrollLeft + delta));
      if (Math.abs(next - node.scrollLeft) < 0.5) return;
      node.scrollLeft = next;
      e.preventDefault();
    };

    let drag = null;
    const swallowClick = (e) => {
      e.stopPropagation();
      e.preventDefault();
    };
    const onPointerDown = (e) => {
      if (e.pointerType !== "mouse" || e.button !== 0 || !overflows()) return;
      drag = { x: e.clientX, left: node.scrollLeft, moved: false };
    };
    const onPointerMove = (e) => {
      if (!drag) return;
      const dx = e.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        drag.moved = true;
        node.style.cursor = "grabbing";
        node.style.userSelect = "none";
      }
      node.scrollLeft = drag.left - dx;
      e.preventDefault();
    };
    const onPointerUp = () => {
      if (!drag) return;
      if (drag.moved) {
        // The click that ends the drag: on the row only, and only this one.
        node.addEventListener("click", swallowClick, {
          capture: true,
          once: true,
        });
        setTimeout(
          () => node.removeEventListener("click", swallowClick, true),
          0
        );
        node.style.cursor = "";
        node.style.userSelect = "";
      }
      drag = null;
    };

    node.addEventListener("wheel", onWheel, { passive: false });
    node.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    return () => {
      node.removeEventListener("wheel", onWheel);
      node.removeEventListener("pointerdown", onPointerDown);
      node.removeEventListener("click", swallowClick, true);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
    };
  }, [scrollView]);
  return setScrollView;
};

export default useMouseScroll;
